import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Atom,
  Map,
  MessageCircle,
  Lightbulb,
  Brain,
  ClipboardCheck,
  Calendar,
  Plus,
  X,
  Sparkles,
  Zap
} from 'lucide-react';

// ============================================
// LEARN-ASSIST MODE DEFINITIONS
// ============================================

export const LEARN_ASSIST_MODES = {
  'first-principles': {
    id: 'first-principles',
    name: 'First-Principles',
    shortName: 'First-Principles',
    description: 'Break down to fundamentals',
    icon: Atom,
    // Premium gradient - Deep Purple
    gradient: 'from-violet-600 via-purple-600 to-indigo-600',
    bgGradient: 'bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600',
    lightBg: 'bg-gradient-to-r from-violet-500/10 via-purple-500/10 to-indigo-500/10',
    borderColor: 'border-violet-500/30',
    textColor: 'text-violet-600',
    // HSL for blending: violet ~270°
    hue: 270,
    triggers: ['first principles', 'first-principles', 'fundamentals', 'break it down', 'basics', 'from scratch'],
    systemPrompt: `You are now in FIRST-PRINCIPLES MODE. Your role is to break down concepts to their absolute fundamentals.

METHODOLOGY:
1. Identify the core concept the student is asking about
2. Strip away all jargon and assumptions
3. Build understanding from the ground up using only fundamental truths
4. Use analogies to everyday experiences
5. Ask probing questions to ensure understanding before building up

STYLE:
- Be Socratic - ask questions that lead to understanding
- Use simple language, avoid technical jargon
- Build concepts layer by layer
- Validate understanding at each step

Always start by identifying what the student already knows, then build from there.`
  },
  
  'fast-context': {
    id: 'fast-context',
    name: 'Fast Context',
    shortName: 'Context',
    description: 'Big picture overview',
    icon: Map,
    // Premium gradient - Ocean Blue
    gradient: 'from-cyan-500 via-blue-500 to-indigo-500',
    bgGradient: 'bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500',
    lightBg: 'bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-indigo-500/10',
    borderColor: 'border-blue-500/30',
    textColor: 'text-blue-600',
    // HSL for blending: blue ~210°
    hue: 210,
    triggers: ['fast context', 'context mode', 'big picture', 'overview', 'how does this fit', 'context'],
    systemPrompt: `You are now in FAST CONTEXT MODE. Your role is to rapidly build mental models and show how concepts connect.

METHODOLOGY:
1. Start with a 30-second overview of the entire topic
2. Show how this topic connects to related concepts
3. Identify what to learn first and what can wait
4. Provide a mental framework for organizing the information

STYLE:
- Be concise and structured
- Use bullet points and numbered lists
- Show relationships between concepts
- Prioritize what matters most

Help the student see the forest before the trees.`
  },
  
  'feynman': {
    id: 'feynman',
    name: 'Feynman Teacher',
    shortName: 'Feynman',
    description: 'Teach to learn',
    icon: MessageCircle,
    // Premium gradient - Emerald Green
    gradient: 'from-emerald-500 via-green-500 to-teal-500',
    bgGradient: 'bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500',
    lightBg: 'bg-gradient-to-r from-emerald-500/10 via-green-500/10 to-teal-500/10',
    borderColor: 'border-green-500/30',
    textColor: 'text-green-600',
    // HSL for blending: green ~150°
    hue: 150,
    triggers: ['feynman', 'feynman mode', 'teach me', 'explain like', 'let me explain', 'teach to learn'],
    systemPrompt: `You are now in FEYNMAN TEACHER MODE. Use the Feynman Technique to help students truly understand.

METHODOLOGY:
1. Ask the student to explain the concept in their own words
2. Identify gaps in their explanation
3. Point out where they used jargon without understanding
4. Guide them to simplify their explanation
5. Have them teach it back to you

STYLE:
- Be encouraging but precise
- Catch every gap in understanding
- Push for simpler explanations
- Celebrate when they nail it

The goal is for the student to explain it so simply a child could understand.`
  },
  
  'examples': {
    id: 'examples',
    name: 'Example Accelerator',
    shortName: 'Examples',
    description: 'Learn through examples',
    icon: Lightbulb,
    // Premium gradient - Sunset Orange/Amber
    gradient: 'from-amber-500 via-orange-500 to-red-500',
    bgGradient: 'bg-gradient-to-r from-amber-500 via-orange-500 to-red-500',
    lightBg: 'bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-red-500/10',
    borderColor: 'border-orange-500/30',
    textColor: 'text-orange-600',
    // HSL for blending: orange ~30°
    hue: 30,
    triggers: ['examples', 'example mode', 'show me examples', 'give me examples', 'concrete examples'],
    systemPrompt: `You are now in EXAMPLE ACCELERATOR MODE. Teach primarily through varied, concrete examples.

METHODOLOGY:
1. Provide the simplest possible example first
2. Add complexity gradually
3. Show edge cases and exceptions
4. Compare and contrast similar examples
5. Let the student derive the rules from patterns

STYLE:
- Lead with examples, follow with theory
- Use real-world analogies
- Show what NOT to do as well
- Build a library of reference examples

The pattern should emerge from the examples, not the other way around.`
  },
  
  'memory': {
    id: 'memory',
    name: 'Memory Lock-In',
    shortName: 'Memory',
    description: 'Memorization techniques',
    icon: Brain,
    // Premium gradient - Hot Pink/Magenta
    gradient: 'from-pink-500 via-rose-500 to-red-400',
    bgGradient: 'bg-gradient-to-r from-pink-500 via-rose-500 to-red-400',
    lightBg: 'bg-gradient-to-r from-pink-500/10 via-rose-500/10 to-red-400/10',
    borderColor: 'border-pink-500/30',
    textColor: 'text-pink-600',
    // HSL for blending: pink ~330°
    hue: 330,
    triggers: ['memory', 'memorize', 'remember', 'memory lock', 'lock in', 'mnemonics'],
    systemPrompt: `You are now in MEMORY LOCK-IN MODE. Help students create lasting memories using proven techniques.

METHODOLOGY:
1. Create memorable mnemonics and acronyms
2. Build memory palaces and visual associations
3. Use spaced repetition patterns
4. Connect new info to existing memories
5. Create emotional hooks and stories

STYLE:
- Make it weird, funny, or visual
- Use acronyms and rhymes
- Create vivid mental images
- Build on what they already know

The more unusual the association, the better it sticks.`
  },
  
  'quiz': {
    id: 'quiz',
    name: 'Rapid Quiz',
    shortName: 'Quiz',
    description: 'Active recall testing',
    icon: ClipboardCheck,
    // Premium gradient - Electric Indigo
    gradient: 'from-indigo-500 via-violet-500 to-purple-500',
    bgGradient: 'bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-500',
    lightBg: 'bg-gradient-to-r from-indigo-500/10 via-violet-500/10 to-purple-500/10',
    borderColor: 'border-indigo-500/30',
    textColor: 'text-indigo-600',
    // HSL for blending: indigo ~240°
    hue: 240,
    triggers: ['quiz', 'quiz me', 'test me', 'rapid test', 'questions', 'practice questions'],
    systemPrompt: `You are now in RAPID QUIZ MODE. Test understanding through rapid-fire questions.

METHODOLOGY:
1. Start with a quick question to assess baseline
2. Adjust difficulty based on responses
3. Mix question types: recall, application, analysis
4. Give immediate, specific feedback
5. Track weak areas and revisit them

STYLE:
- Keep questions focused and clear
- Provide instant feedback
- Explain why answers are right/wrong
- Gradually increase difficulty

After each answer, provide feedback then immediately ask the next question.`
  },
  
  'plan': {
    id: 'plan',
    name: '30-Day Plan',
    shortName: 'Plan',
    description: 'Study schedule',
    icon: Calendar,
    // Premium gradient - Forest/Teal
    gradient: 'from-teal-500 via-emerald-500 to-green-500',
    bgGradient: 'bg-gradient-to-r from-teal-500 via-emerald-500 to-green-500',
    lightBg: 'bg-gradient-to-r from-teal-500/10 via-emerald-500/10 to-green-500/10',
    borderColor: 'border-teal-500/30',
    textColor: 'text-teal-600',
    // HSL for blending: teal ~180°
    hue: 180,
    triggers: ['plan', '30 day', 'study plan', 'schedule', 'roadmap', 'learning plan'],
    systemPrompt: `You are now in 30-DAY PLAN MODE. Create actionable, personalized study schedules.

METHODOLOGY:
1. Assess current knowledge level
2. Identify target goals and timeline
3. Break down into daily/weekly milestones
4. Include review sessions and practice
5. Build in flexibility and checkpoints

STYLE:
- Be specific with time allocations
- Include concrete actions, not vague goals
- Balance learning with review
- Make it realistic and sustainable

Create a plan that builds momentum and shows progress.`
  }
};

// ============================================
// COLOR BLENDING UTILITIES
// ============================================

// Convert HSL to RGB
function hslToRgb(h, s, l) {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = n => {
    const k = (n + h / 30) % 12;
    return l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
  };
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
}

// Convert RGB to HSL
function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

// Blend multiple hues to create a combined gradient
export function blendModeColors(modeIds) {
  if (!modeIds || modeIds.length === 0) return null;
  if (modeIds.length === 1) {
    const mode = LEARN_ASSIST_MODES[modeIds[0]];
    return mode ? mode.bgGradient : null;
  }
  
  // Get all hues
  const hues = modeIds
    .map(id => LEARN_ASSIST_MODES[id]?.hue)
    .filter(h => h !== undefined);
  
  if (hues.length === 0) return null;
  
  // Calculate average hue (handling circular average)
  let sinSum = 0, cosSum = 0;
  hues.forEach(h => {
    sinSum += Math.sin(h * Math.PI / 180);
    cosSum += Math.cos(h * Math.PI / 180);
  });
  const avgHue = Math.atan2(sinSum, cosSum) * 180 / Math.PI;
  const normalizedHue = avgHue < 0 ? avgHue + 360 : avgHue;
  
  // Create gradient stops based on blended hue
  const [r1, g1, b1] = hslToRgb(normalizedHue - 20, 70, 55);
  const [r2, g2, b2] = hslToRgb(normalizedHue, 70, 50);
  const [r3, g3, b3] = hslToRgb(normalizedHue + 20, 70, 55);
  
  return `bg-gradient-to-r from-[rgb(${r1},${g1},${b1})] via-[rgb(${r2},${g2},${b2})] to-[rgb(${r3},${g3},${b3})]`;
}

// ============================================
// NATURAL LANGUAGE DETECTION
// ============================================

export function detectModesFromText(text) {
  if (!text) return [];
  
  const lowerText = text.toLowerCase().trim();
  const detectedModes = [];
  
  for (const [modeId, mode] of Object.entries(LEARN_ASSIST_MODES)) {
    for (const trigger of mode.triggers) {
      if (lowerText.includes(trigger)) {
        detectedModes.push(modeId);
        break; // Only add each mode once
      }
    }
  }
  
  return detectedModes;
}

// Check if text is a mode-only command (e.g., just "feynman mode")
export function isModeCommand(text) {
  if (!text) return false;
  const lowerText = text.toLowerCase().trim();
  
  // Check for explicit mode commands
  const modeCommandPatterns = [
    /^(\w+)\s*mode$/i,
    /^activate\s+(\w+)$/i,
    /^switch\s+to\s+(\w+)$/i,
    /^use\s+(\w+)$/i,
    /^(\w+)\s+mode\s+on$/i,
  ];
  
  for (const pattern of modeCommandPatterns) {
    if (pattern.test(lowerText)) return true;
  }
  
  // Check if the entire text is just a trigger
  for (const mode of Object.values(LEARN_ASSIST_MODES)) {
    for (const trigger of mode.triggers) {
      if (lowerText === trigger || lowerText === `${trigger} mode`) {
        return true;
      }
    }
  }
  
  return false;
}

// ============================================
// MODE PICKER COMPONENT
// ============================================

export function ModePickerMenu({ activeModes, onToggleMode, onClearModes }) {
  const [isOpen, setIsOpen] = useState(false);
  
  const modeArray = Object.values(LEARN_ASSIST_MODES);
  
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-xl hover:bg-primary/10"
        >
          <Plus className="w-5 h-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-3" 
        align="start"
        side="top"
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="font-medium text-sm">Learn-assist™ Modes</span>
            </div>
            {activeModes.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearModes}
                className="h-7 text-xs text-muted-foreground"
              >
                Clear all
              </Button>
            )}
          </div>
          
          <p className="text-xs text-muted-foreground">
            Select modes to change how I help you learn. Combine multiple modes!
          </p>
          
          <div className="grid grid-cols-1 gap-2">
            {modeArray.map((mode) => {
              const Icon = mode.icon;
              const isActive = activeModes.includes(mode.id);
              
              return (
                <button
                  key={mode.id}
                  onClick={() => {
                    onToggleMode(mode.id);
                  }}
                  className={`flex items-center gap-3 p-2.5 rounded-xl transition-all text-left ${
                    isActive 
                      ? `${mode.lightBg} ${mode.borderColor} border-2` 
                      : 'hover:bg-muted border-2 border-transparent'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-lg ${mode.bgGradient} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-4.5 h-4.5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-sm ${isActive ? mode.textColor : ''}`}>
                      {mode.name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {mode.description}
                    </p>
                  </div>
                  {isActive && (
                    <Zap className={`w-4 h-4 ${mode.textColor}`} />
                  )}
                </button>
              );
            })}
          </div>
          
          <p className="text-[10px] text-muted-foreground pt-2 border-t border-border">
            Tip: Type "feynman mode" or "quiz me" to activate modes with natural language!
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ============================================
// ACTIVE MODE INDICATOR
// ============================================

export function ActiveModeIndicator({ activeModes, onRemoveMode, onClearAll }) {
  if (!activeModes || activeModes.length === 0) return null;
  
  const gradientClass = blendModeColors(activeModes);
  
  return (
    <div className={`relative overflow-hidden rounded-xl mb-3`}>
      {/* Gradient background */}
      <div className={`absolute inset-0 ${gradientClass} opacity-10`} />
      
      <div className="relative p-3 flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <div className={`w-2 h-2 rounded-full ${gradientClass}`} />
          <span className="text-xs font-medium text-muted-foreground">Active:</span>
          {activeModes.map((modeId) => {
            const mode = LEARN_ASSIST_MODES[modeId];
            if (!mode) return null;
            const Icon = mode.icon;
            
            return (
              <Badge
                key={modeId}
                variant="secondary"
                className={`${mode.lightBg} ${mode.borderColor} border pr-1 gap-1`}
              >
                <Icon className={`w-3 h-3 ${mode.textColor}`} />
                <span className={mode.textColor}>{mode.shortName}</span>
                <button
                  onClick={() => onRemoveMode(modeId)}
                  className="ml-1 hover:bg-background/50 rounded p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            );
          })}
        </div>
        
        {activeModes.length > 1 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            className="h-6 text-xs"
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}

// ============================================
// BUILD SYSTEM PROMPT FROM ACTIVE MODES
// ============================================

export function buildModeSystemPrompt(activeModes, basePrompt = '') {
  if (!activeModes || activeModes.length === 0) {
    return basePrompt;
  }
  
  const modePrompts = activeModes
    .map(id => LEARN_ASSIST_MODES[id]?.systemPrompt)
    .filter(Boolean);
  
  if (modePrompts.length === 0) {
    return basePrompt;
  }
  
  const combinedModePrompt = modePrompts.join('\n\n---\n\n');
  
  if (activeModes.length > 1) {
    return `${basePrompt}

You are operating in COMBINED MODE with the following methodologies active. Blend these approaches naturally:

${combinedModePrompt}

IMPORTANT: Seamlessly integrate all active modes in your responses. Don't compartmentalize - let the methodologies complement each other.`;
  }
  
  return `${basePrompt}

${combinedModePrompt}`;
}

// Export all modes as array for iteration
export const MODES_ARRAY = Object.values(LEARN_ASSIST_MODES);
